import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Button } from 'react-bootstrap';
import SuperAdminDashboardStats from './SuperAdminDashboardStats';
import PlayerStatusTable from '../PlayerStatusTable';
import TransactionTable from '../TransactionTable';
import RecentTeamsTable from '../RecentTeamsTable';
import RecentClubsTable from '../RecentClubsTable';
import ShimmerLoader from '../ShimmerLoader';
import { getDashboardData, getSuperAdminDashboardStats, getSuperAdminRecentTransactions, getSuperAdminRecentPlayers, getSuperAdminRecentTeams, getSuperAdminRecentClubs, DashboardData } from '../../api/dashboard/dashboardService';
import './SuperAdminDashboard.css';

const SuperAdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      // Priority 1: Try to fetch stats from the new API endpoint first
      let superAdminStats = null;
      try {
        superAdminStats = await getSuperAdminDashboardStats();
      } catch (statsError) {
        console.warn('Failed to fetch super admin stats from new API:', statsError);
      }
      
      // Priority 2: Try to fetch recent transactions from the new API endpoint
      let superAdminTransactions = null;
      try {
        superAdminTransactions = await getSuperAdminRecentTransactions();
      } catch (transactionsError) {
        console.warn('Failed to fetch super admin recent transactions from new API:', transactionsError);
      }
      
      // Priority 3: Try to fetch recent players from the new API endpoint
      let superAdminPlayers = null;
      try {
        superAdminPlayers = await getSuperAdminRecentPlayers();
      } catch (playersError) {
        console.warn('Failed to fetch super admin recent players from new API:', playersError);
      }
      
      // Priority 4: Try to fetch recent teams from the new API endpoint
      let superAdminTeams = null;
      try {
        superAdminTeams = await getSuperAdminRecentTeams();
      } catch (teamsError) {
        console.warn('Failed to fetch super admin recent teams from new API:', teamsError);
      }
      
      // Priority 5: Try to fetch recent clubs from the new API endpoint
      let superAdminClubs = null;
      try {
        superAdminClubs = await getSuperAdminRecentClubs();
      } catch (clubsError) {
        console.warn('Failed to fetch super admin recent clubs from new API:', clubsError);
      }
      
      // Priority 6: Fetch other dashboard data
      let data = await getDashboardData('all');
      
      // Priority 7: Use real API stats if available, otherwise keep existing stats
      if (superAdminStats) {
        data.stats = superAdminStats;
      } else {
      }
      
      // Priority 8: Use real API recent transactions if available, otherwise keep existing transactions
      if (superAdminTransactions) {
        data.recentTransactions = superAdminTransactions;
      } else {
      }
      
      // Priority 9: Use real API recent players if available, otherwise keep existing players
      if (superAdminPlayers) {
        data.recentPlayers = superAdminPlayers;
      } else {
      }
      
      // Priority 10: Use real API recent teams if available, otherwise keep existing teams
      if (superAdminTeams) {
        data.recentTeams = superAdminTeams;
      } else {
      }
      
      // Priority 11: Use real API recent clubs if available, otherwise keep existing clubs
      if (superAdminClubs) {
        data.recentClubs = superAdminClubs;
      } else {
      }
      
      
      // Only use mock data if both APIs fail
      if (!superAdminStats && (!data.stats || Object.values(data.stats).every(val => val === 0))) {
        console.warn('Both APIs failed, using mock data');
        setError('Failed to load real data — showing demo data');
        data = getMockDashboardData();
      }
      
      setDashboardData(data);
    } catch (err) {
      console.error('Super Admin Dashboard data fetch error:', err);
      setError('Failed to load dashboard data — showing demo data');
      // Only use mock data as last resort
      setDashboardData(getMockDashboardData());
    } finally {
      setLoading(false);
    }
  };

  // Mock data function for super admin
  const getMockDashboardData = (): DashboardData => {
    return {
      stats: {
        teams: 156,
        players: 25,
        tournaments: 89,
        payments: 48275,
        activePlayers: 11,
        inactivePlayers: 0,
        activeTeams: 120,
        inactiveTeams: 36,
        // New API fields
        totalAmount: 48275,
        totalDues: 4755,
        totalPlayers: 25,
        approvedPlayers: 11,
        rejectedPlayers: 0
      },
      upcomingTournaments: [
        {
          _id: '1',
          name: 'SFL Football Championship 2024',
          sport: 'Football',
          ageCategories: ['U7', 'U10', 'U12', 'U15', 'U18'],
          startDate: '2024-08-12',
          location: 'Multiple Venues, Bengaluru',
          status: 'upcoming',
          registeredTeams: 45,
          maxTeams: 80
        },
        {
          _id: '2',
          name: 'SFL Youth League',
          sport: 'Football',
          ageCategories: ['U9', 'U11', 'U13', 'U16'],
          startDate: '2024-08-15',
          location: 'Koramangala Stadium, Bengaluru',
          status: 'upcoming',
          registeredTeams: 32,
          maxTeams: 60
        },
        {
          _id: '3',
          name: 'SFL Premier Cup',
          sport: 'Football',
          ageCategories: ['U8', 'U10', 'U12', 'U14'],
          startDate: '2024-08-18',
          location: 'HSR Layout Ground, Bengaluru',
          status: 'upcoming',
          registeredTeams: 28,
          maxTeams: 50
        }
      ],
      ongoingTournaments: [
        {
          _id: '4',
          name: 'SFL Summer League',
          sport: 'Football',
          ageCategories: ['U12', 'U15'],
          startDate: '2024-07-01',
          location: 'Electronic City Ground, Bengaluru',
          status: 'ongoing',
          registeredTeams: 24,
          maxTeams: 40
        }
      ],
      recentTransactions: [
        {
          _id: '68a066001dc05829f33d3def',
          transactionId: 'pay_R5z1eNxlXU05RW',
          tournamentName: 'SFL Orange League R2',
          teamName: 'Phoenix Sports Club',
          date: '2025-08-16T11:05:51.051Z',
          ageCategory: 'Junior Boys',
          amount: 9500,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05e32574860a23222fcd4',
          transactionId: 'pay_R5ySPtifY6oNQb',
          tournamentName: 'SFL Orange League R1',
          teamName: 'Blaze United FC',
          date: '2025-08-16T10:32:29.837Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05e084c4e3e93f2526c01',
          transactionId: 'pay_R5yRexHwjziXDd',
          tournamentName: 'SFL Orange League R1',
          teamName: 'Stormbreak Academy',
          date: '2025-08-16T10:31:46.682Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05d89574860a23222fca6',
          transactionId: 'pay_R5yPRLVjs1N0Sj',
          tournamentName: 'SFL Orange League R1',
          teamName: 'Ironclad Warriors',
          date: '2025-08-16T10:29:40.762Z',
          ageCategory: 'U10',
          amount: 7000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05d331dc05829f33d3cfe',
          transactionId: 'pay_R5yNwfjMjiQF6M',
          tournamentName: 'SFL Orange League R1',
          teamName: 'Lightning Bolts',
          date: '2025-08-16T10:28:16.096Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        }
      ],
      recentPlayers: [
        {
          _id: '1',
          name: 'Rahul Sharma',
          age: 12,
          team: 'Phoenix U12',
          club: 'Phoenix Sports Club',
          status: 'approved',
          registrationDate: '2024-08-15'
        },
        {
          _id: '2',
          name: 'Priya Patel',
          age: 10,
          team: 'Blaze U10',
          club: 'Blaze United FC',
          status: 'pending',
          registrationDate: '2024-08-14'
        },
        {
          _id: '3',
          name: 'Arjun Singh',
          age: 14,
          team: 'Storm U14',
          club: 'Stormbreak Academy',
          status: 'approved',
          registrationDate: '2024-08-13'
        },
        {
          _id: '4',
          name: 'Zara Khan',
          age: 11,
          team: 'Ironclad U11',
          club: 'Ironclad Warriors',
          status: 'rejected',
          registrationDate: '2024-08-12'
        },
        {
          _id: '5',
          name: 'Vikram Reddy',
          age: 13,
          team: 'Lightning U13',
          club: 'Lightning Bolts',
          status: 'approved',
          registrationDate: '2024-08-11'
        }
      ]
    };
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container fluid>
        {/* Shimmer loader for super admin header */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-3">
            <div className="shimmer-text" style={{ width: '400px', height: '32px' }}></div>
          </div>
        </div>
        
        {/* Shimmer loader for stats */}
        <div className="mb-4">
          <ShimmerLoader type="stats" count={8} />
        </div>
        
        {/* Shimmer loader for tournaments */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="shimmer-text" style={{ width: '200px', height: '24px' }}></div>
            <div className="shimmer-text" style={{ width: '100px', height: '16px' }}></div>
          </div>
          <Row>
            {[1, 2, 3].map((index) => (
              <Col lg={4} md={6} key={index} className="mb-3">
                <ShimmerLoader type="card" height="400px" />
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    );
  }

  if (error && !dashboardData) {
    return (
      <Container fluid>
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Error Loading Dashboard</Alert.Heading>
          <p>{error}</p>
          <hr />
          <Button variant="outline-danger" onClick={fetchDashboardData}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container fluid>
        <Alert variant="warning" className="mt-3">
          No dashboard data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '0 15px', marginTop: '20px' }}>
      {/* Super Admin Header */}
      <div className="mb-4" style={{ padding: '0 15px' }}>
        <div className="d-flex align-items-center">
          <div>
            <h2 className="mb-0 fw-bold" style={{ color: '#212529', fontSize: '1.75rem', lineHeight: '1.2' }}>
              SFL Admin Dashboard
            </h2>
            <p className="text-muted mb-0" style={{ fontSize: '1rem' }}>
              Complete system overview and management
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Overview Cards */}
      <SuperAdminDashboardStats stats={dashboardData.stats} />

      {/* Upcoming Tournaments Section - Removed for Super Admin Dashboard */}

      {/* Recent Transactions Section */}
      <Row className="mb-4">
        <Col lg={12}>
          <TransactionTable
            title="Latest Transactions"
            transactions={dashboardData.recentTransactions}
            viewAllLink="/super-admin-payments"
            maxRows={5}
            isDashboard={true}
          />
        </Col>
      </Row>

      {/* Recent Players Status Section */}
      <Row className="mb-4">
        <Col lg={12}>
          <PlayerStatusTable
            players={dashboardData.recentPlayers}
            viewAllLink="/manage-players"
          />
        </Col>
      </Row>

      {/* Recent Teams Section */}
      <Row className="mb-4">
        <Col lg={12}>
          <RecentTeamsTable
            teams={dashboardData.recentTeams || []}
            loading={loading}
          />
        </Col>
      </Row>

      {/* Recent Clubs Section */}
      <Row className="mb-4">
        <Col lg={12}>
          <RecentClubsTable
            clubs={dashboardData.recentClubs || []}
            loading={loading}
          />
        </Col>
      </Row>


    </Container>
  );
};

export default SuperAdminDashboard; 